import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getCurrentSalonId } from "../../utils/currentSalon";
import { listSalonUsers } from "../../api/salonUsers";
import {
  addFeedbackFollowupNote,
  assignFeedbackFollowup,
  getFeedbackFollowup,
  listFeedbackTemplates,
  sendFeedbackFollowupEmail,
  updateFeedbackFollowupStatus,
} from "../../api/marketingFeedback";
import { formatDateTimeInTimeZone, formatDateInTimeZone } from "../../utils/datetime";
import { getSalonTimezone } from "../../utils/salonTimezone";
import InfoTooltip from "../../components/common/InfoTooltip";
import "../MarketingSectionCompact.css";

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "resolved", label: "Resolved" },
  { value: "dismissed", label: "Dismissed" },
];

const RESOLUTION_CODES = [
  { value: "", label: "Select resolution" },
  { value: "contacted_client", label: "Contacted client" },
  { value: "refund", label: "Refund" },
  { value: "redo_service", label: "Redo service" },
  { value: "discount_given", label: "Discount given" },
  { value: "explained_policy", label: "Explained policy" },
  { value: "no_action", label: "No action" },
  { value: "spam", label: "Spam" },
  { value: "other", label: "Other" },
];

const TEMPLATE_TOKENS = [
  "{{client_full_name}}",
  "{{client_phone}}",
  "{{ticket_number}}",
  "{{service_date_local}}",
  "{{staff_name}}",
  "{{manager_name}}",
  "{{salon_name}}",
  "{{salon_phone}}",
];

const renderTokens = (template, values) =>
  String(template || "").replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (match, key) =>
    values?.[key] ? String(values[key]) : ""
  );

const getUserId = (user) => user?.user_id ?? user?.id ?? null;

const getUserName = (user) =>
  user?.full_name || user?.name || user?.username || user?.email || "";

export default function FeedbackFollowupDetail() {
  const { followupId } = useParams();
  const navigate = useNavigate();
  const { user, token, currentSalon } = useAuth();
  const salonId = getCurrentSalonId(user);
  const salonTimezone = useMemo(() => getSalonTimezone(currentSalon), [currentSalon]);

  const [followup, setFollowup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [templates, setTemplates] = useState([]);
  const [salonUsers, setSalonUsers] = useState([]);
  const [assignUserId, setAssignUserId] = useState("");
  const [statusForm, setStatusForm] = useState({
    status: "open",
    resolution_code: "",
    resolution_notes: "",
  });
  const [noteText, setNoteText] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [composer, setComposer] = useState({ subject: "", body: "" });
  const [sendState, setSendState] = useState({ sending: false, message: "" });

  const loadFollowup = useCallback(async () => {
    if (!followupId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getFeedbackFollowup(followupId, token);
      setFollowup(data || null);
      setAssignUserId(String(data?.assigned_to_user_id || ""));
      setStatusForm({
        status: data?.status || "open",
        resolution_code: data?.resolution_code || "",
        resolution_notes: data?.resolution_notes || "",
      });
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load follow-up.");
      setFollowup(null);
    } finally {
      setLoading(false);
    }
  }, [followupId, token]);

  const loadTemplates = useCallback(async () => {
    if (!salonId) return;
    try {
      const data = await listFeedbackTemplates(salonId, token);
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      setTemplates([]);
    }
  }, [salonId, token]);

  const loadUsers = useCallback(async () => {
    try {
      const data = await listSalonUsers(token);
      setSalonUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setSalonUsers([]);
    }
  }, [token]);

  useEffect(() => {
    loadFollowup();
    loadTemplates();
    loadUsers();
  }, [loadFollowup, loadTemplates, loadUsers]);

  const handleAssign = async () => {
    if (!followup) return;
    try {
      const parsedUserId = assignUserId ? Number(assignUserId) : null;
      if (assignUserId && !Number.isFinite(parsedUserId)) {
        setError("Please choose a valid staff member.");
        return;
      }
      const payload = {
        assigned_to_user_id: parsedUserId,
      };
      await assignFeedbackFollowup(followup.id, payload, token);
      await loadFollowup();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to update assignee.");
    }
  };

  const handleStatusUpdate = async () => {
    if (!followup) return;
    try {
      await updateFeedbackFollowupStatus(followup.id, statusForm, token);
      await loadFollowup();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to update status.");
    }
  };

  const handleAddNote = async () => {
    if (!followup || !noteText.trim()) return;
    setNoteSaving(true);
    try {
      await addFeedbackFollowupNote(
        followup.id,
        { note: noteText.trim() },
        token
      );
      setNoteText("");
      await loadFollowup();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to add note.");
    } finally {
      setNoteSaving(false);
    }
  };

  const handleSendEmail = async () => {
    if (!followup) return;
    if (!composer.subject.trim() || !composer.body.trim()) {
      setSendState({ sending: false, message: "Subject and body are required." });
      return;
    }
    setSendState({ sending: true, message: "" });
    try {
      await sendFeedbackFollowupEmail(
        followup.id,
        {
          subject: composer.subject,
          body: composer.body,
        },
        token
      );
      setSendState({ sending: false, message: "Email sent." });
      await loadFollowup();
    } catch (err) {
      setSendState({
        sending: false,
        message: err?.response?.data?.detail || "Unable to send email.",
      });
    }
  };

  const resolvedTemplates = useMemo(
    () => (Array.isArray(templates) ? templates : []),
    [templates]
  );
  const resolvedUsers = useMemo(
    () => (Array.isArray(salonUsers) ? salonUsers : []),
    [salonUsers]
  );
  const selectedAssignee = useMemo(
    () =>
      resolvedUsers.find(
        (item) => String(getUserId(item) || "") === String(assignUserId || "")
      ),
    [resolvedUsers, assignUserId]
  );

  const tokenContext = useMemo(() => {
    const ticketNumber = followup?.ticket_number || followup?.ticket_id || "";
    const submittedAt = followup?.review_submitted_at_utc;
    const serviceDate = submittedAt
      ? formatDateInTimeZone(submittedAt, salonTimezone)
      : "--";
    return {
      client_full_name: followup?.client_name || "",
      client_phone: followup?.client_phone || "",
      ticket_number: ticketNumber,
      service_date_local: serviceDate,
      staff_name: followup?.technician_name || "",
      manager_name:
        getUserName(selectedAssignee) ||
        followup?.assigned_to_name ||
        getUserName(user) ||
        currentSalon?.owner_name ||
        currentSalon?.name ||
        currentSalon?.salon_name ||
        "",
      salon_name:
        currentSalon?.name ||
        currentSalon?.salon_name ||
        user?.salon_name ||
        "Salon",
      salon_phone:
        currentSalon?.phone ||
        currentSalon?.salon_phone ||
        currentSalon?.business_phone ||
        "",
    };
  }, [followup, salonTimezone, currentSalon, user, selectedAssignee]);

  const previewSubject = renderTokens(composer.subject, tokenContext);
  const previewBody = renderTokens(composer.body, tokenContext);

  return (
    <div className="marketingSectionShell">
      <div className="marketingSectionWide marketing-section-wide marketing-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Follow-up Detail
            </h1>
            <button
              type="button"
              onClick={() => navigate("/manager/marketing/feedback-followups")}
              className="rounded-full border border-white/60 px-4 py-2 text-xs md:text-sm font-semibold text-white hover:bg-white/10"
            >
              Back to inbox
            </button>
          </div>
          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}
            {loading && (
              <div className="text-sm text-slate-500">Loading follow-up...</div>
            )}
            {!loading && followup && (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_0.9fr]">
                <div className="space-y-6">
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Summary
                        </div>
                        <div className="text-lg font-semibold text-slate-900">
                          Ticket {followup.ticket_number || followup.ticket_id}
                        </div>
                      </div>
                      {followup.overdue && (
                        <span className="rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-700">
                          Overdue
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <div className="text-xs text-slate-500">Client</div>
                        <div className="text-sm font-semibold text-slate-900">
                          {followup.client_name || "Client"}
                        </div>
                        <div className="text-xs text-slate-500">
                          {followup.client_phone || "No phone"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Staff</div>
                        <div className="text-sm font-semibold text-slate-900">
                          {followup.technician_name || "--"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Category</div>
                        <div className="text-sm text-slate-700">
                          {Array.isArray(followup.category_labels) &&
                          followup.category_labels.length
                            ? followup.category_labels.join(", ")
                            : "--"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 inline-flex items-center gap-2">
                          Submitted
                          <InfoTooltip text="Salon local time" label="Salon local time" />
                        </div>
                        <div className="text-sm text-slate-700">
                          {followup.review_submitted_at_utc
                            ? formatDateTimeInTimeZone(
                                followup.review_submitted_at_utc,
                                salonTimezone
                              )
                            : "--"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">
                          SLA Due
                          <span className="ml-2">
                            <InfoTooltip text="Salon local time" label="Salon local time" />
                          </span>
                        </div>
                        <div className="text-sm text-slate-700">
                          {followup.sla_due_at_utc
                            ? formatDateTimeInTimeZone(
                                followup.sla_due_at_utc,
                                salonTimezone
                              )
                            : "--"}
                        </div>
                      </div>
                    </div>
                    <div>
                      <a
                        href={`/tickets/${followup.ticket_id}`}
                        className="text-sm font-semibold text-rose-600 hover:text-rose-500"
                      >
                        View ticket
                      </a>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
                    <div className="text-base font-semibold text-slate-900">
                      Client Feedback
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm text-slate-700">
                      <div>
                        <div className="text-xs text-slate-500">Overall</div>
                        <div className="font-semibold">
                          {followup.overall_rating ?? "--"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Salon</div>
                        <div className="font-semibold">
                          {followup.salon_rating ?? "--"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Reception</div>
                        <div className="font-semibold">
                          {followup.reception_rating ?? "--"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Service</div>
                        <div className="font-semibold">
                          {followup.service_rating ?? "--"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Staff</div>
                        <div className="font-semibold">
                          {followup.staff_rating ?? "--"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Online booking</div>
                        <div className="font-semibold">
                          {followup.online_booking_rating ?? "--"}
                        </div>
                      </div>
                    </div>
                    {followup.comment && (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                        {followup.comment}
                      </div>
                    )}
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                    <div className="text-base font-semibold text-slate-900">
                      Timeline & Notes
                    </div>
                    <div className="space-y-3">
                      {Array.isArray(followup.notes) && followup.notes.length > 0 ? (
                        followup.notes.map((note) => (
                          <div
                            key={note.id}
                            className="rounded-xl border border-slate-200 bg-white p-3"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                              <span>{note.author_name || "System"}</span>
                              <span>
                                {note.created_at_utc
                                  ? formatDateTimeInTimeZone(
                                      note.created_at_utc,
                                      salonTimezone
                                    )
                                  : "--"}
                              </span>
                            </div>
                            <div className="mt-2 text-sm text-slate-700">
                              {note.note}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-slate-500">
                          No notes yet.
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <textarea
                        value={noteText}
                        onChange={(event) => setNoteText(event.target.value)}
                        rows={3}
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                        placeholder="Add a note for the follow-up timeline."
                      />
                      <button
                        type="button"
                        onClick={handleAddNote}
                        disabled={noteSaving}
                        className="rounded-full bg-slate-900 px-4 py-2 text-xs md:text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                      >
                        {noteSaving ? "Saving..." : "Add note"}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                    <div className="text-base font-semibold text-slate-900">
                      Actions
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-semibold uppercase text-slate-500">
                          Assign to
                        </label>
                        <div className="flex gap-2">
                          <select
                            value={assignUserId}
                            onChange={(event) => setAssignUserId(event.target.value)}
                            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                          >
                            <option value="">Unassigned</option>
                            {resolvedUsers.map((item) => {
                              const userId = getUserId(item);
                              if (userId == null) return null;
                              return (
                                <option key={userId} value={userId}>
                                  {getUserName(item)}
                                </option>
                              );
                            })}
                          </select>
                          <button
                            type="button"
                            onClick={handleAssign}
                            className="rounded-full border border-slate-300 px-4 py-2 text-xs md:text-sm font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Update
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-semibold uppercase text-slate-500">
                          Status
                        </label>
                        <select
                          value={statusForm.status}
                          onChange={(event) =>
                            setStatusForm((prev) => ({
                              ...prev,
                              status: event.target.value,
                            }))
                          }
                          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                        >
                          {STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <select
                          value={statusForm.resolution_code}
                          onChange={(event) =>
                            setStatusForm((prev) => ({
                              ...prev,
                              resolution_code: event.target.value,
                            }))
                          }
                          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                        >
                          {RESOLUTION_CODES.map((option) => (
                            <option key={option.value || "none"} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <textarea
                          value={statusForm.resolution_notes}
                          onChange={(event) =>
                            setStatusForm((prev) => ({
                              ...prev,
                              resolution_notes: event.target.value,
                            }))
                          }
                          rows={3}
                          className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                          placeholder="Resolution notes"
                        />
                        <button
                          type="button"
                          onClick={handleStatusUpdate}
                          className="rounded-full bg-rose-500 px-4 py-2 text-xs md:text-sm font-semibold text-white hover:bg-rose-600"
                        >
                          Save status
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                    <div className="text-base font-semibold text-slate-900">
                      Templates
                    </div>
                    <div className="space-y-2">
                      {resolvedTemplates.length === 0 && (
                        <div className="text-sm text-slate-500">
                          No templates yet.
                        </div>
                      )}
                      {resolvedTemplates.map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() =>
                            setComposer({
                              subject: template.subject || "",
                              body: template.body || "",
                            })
                          }
                          className="w-full text-left rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                        >
                          <div className="font-semibold text-slate-900">
                            {template.name}
                          </div>
                          <div className="text-xs text-slate-500">
                            {template.subject || "No subject"}
                          </div>
                        </button>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {TEMPLATE_TOKENS.map((token) => (
                        <button
                          key={token}
                          type="button"
                          onClick={() =>
                            setComposer((prev) => ({
                              ...prev,
                              body: `${prev.body || ""}${prev.body ? "\n" : ""}${token}`,
                            }))
                          }
                          className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                        >
                          {token}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                    <div className="text-base font-semibold text-slate-900">
                      Email Composer
                    </div>
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={composer.subject}
                        onChange={(event) =>
                          setComposer((prev) => ({
                            ...prev,
                            subject: event.target.value,
                          }))
                        }
                        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                        placeholder="Subject"
                      />
                      <textarea
                        value={composer.body}
                        onChange={(event) =>
                          setComposer((prev) => ({
                            ...prev,
                            body: event.target.value,
                          }))
                        }
                        rows={6}
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                        placeholder="Write your response..."
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="text-xs font-semibold uppercase text-slate-500">
                        Preview
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 whitespace-pre-wrap">
                        <div className="font-semibold">{previewSubject}</div>
                        <div className="mt-2">{previewBody}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => navigator.clipboard.writeText(composer.body || "")}
                        className="rounded-full border border-slate-300 px-4 py-2 text-xs md:text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Copy to clipboard
                      </button>
                      <button
                        type="button"
                        onClick={handleSendEmail}
                        disabled={sendState.sending}
                        className="rounded-full bg-rose-500 px-4 py-2 text-xs md:text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                      >
                        {sendState.sending ? "Sending..." : "Send email"}
                      </button>
                    </div>
                    {sendState.message && (
                      <div className="text-sm text-slate-600">{sendState.message}</div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {!loading && !followup && (
              <div className="text-sm text-slate-500">Follow-up not found.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
